import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Auth + onboarding (rendered bare — no console shell). PRESERVED flow.
import LoginScreen from './pages/auth/LoginScreen';
import OTPScreen from './pages/auth/OTPScreen';
import SearchBusinessScreen from './pages/business/SearchBusinessScreen';
import AddBusinessScreen from './pages/business/AddBusinessScreen';
import EditBusinessScreen from './pages/business/EditBusinessScreen';

// Console shell + guards
import ConsoleLayout from './layout/ConsoleLayout';
import ProtectedRoute from './auth/ProtectedRoute';
import DashboardPage from './pages/dashboard/DashboardPage';
import RevenuePage from './pages/dashboard/RevenuePage';
import QueuesListPage from './pages/queues/QueuesListPage';
import QueueFormPage from './pages/queues/QueueFormPage';
import QueueDetailsPage from './pages/queues/QueueDetailsPage';
import ConsultationScreen from './pages/queues/ConsultationScreen';
import TeamPage from './pages/team/TeamPage';
import HolidaysPage from './pages/holidays/HolidaysPage';
import ProfilePage from './pages/profile/ProfilePage';
import BusinessDetailsPage from './pages/business/BusinessDetailsPage';
import SubscriptionsPage from './pages/billing/SubscriptionsPage';
import PlansPage from './pages/billing/PlansPage';
import PaymentsPage from './pages/billing/PaymentsPage';

/**
 * Routing
 * --------
 * Two zones:
 *  1. Bare zone — login / OTP / business onboarding (self-guarded, no shell).
 *  2. Console zone — everything under <ConsoleLayout/>, guarded by <ProtectedRoute/>.
 *
 * All modules (1–9 per MODULE_API_PLAN.md) are wired to real APIs. Unknown paths
 * redirect to /dashboard (which itself bounces to /login when unauthenticated).
 */
function App(): React.ReactElement {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* ---- Bare: auth + onboarding (do not change the flow) ---- */}
        <Route path="/" element={<LoginScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/otp" element={<OTPScreen />} />
        <Route path="/search-business" element={<SearchBusinessScreen />} />
        <Route path="/add-business" element={<AddBusinessScreen />} />
        <Route path="/edit-business" element={<EditBusinessScreen />} />

        {/* ---- Console (protected) ---- */}
        <Route
          element={
            <ProtectedRoute>
              <ConsoleLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard + Revenue (admin/administrator only — providers use Queues) */}
          <Route path="/dashboard" element={<ProtectedRoute adminOnly redirect="/queues"><DashboardPage /></ProtectedRoute>} />
          <Route path="/revenue" element={<ProtectedRoute adminOnly redirect="/queues"><RevenuePage /></ProtectedRoute>} />

          {/* Queues + tokens */}
          <Route path="/queues" element={<QueuesListPage />} />
          <Route path="/queues/new" element={<QueueFormPage />} />
          <Route path="/queues/:id" element={<QueueDetailsPage />} />
          <Route path="/queues/:id/edit" element={<QueueFormPage />} />
          <Route path="/queues/:id/consultation/:tokenId" element={<ConsultationScreen />} />

          {/* Team */}
          <Route
            path="/team"
            element={
              <ProtectedRoute permission="can_access_team_tab">
                <TeamPage />
              </ProtectedRoute>
            }
          />

          {/* Holidays */}
          <Route
            path="/holidays"
            element={
              <ProtectedRoute permission="can_set_holidays">
                <HolidaysPage />
              </ProtectedRoute>
            }
          />

          {/* Billing (Administrator only) */}
          <Route path="/subscriptions" element={<ProtectedRoute administratorOnly><SubscriptionsPage /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute administratorOnly><PlansPage /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute administratorOnly><PaymentsPage /></ProtectedRoute>} />

          {/* Account */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route
            path="/business"
            element={
              <ProtectedRoute administratorOnly>
                <BusinessDetailsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Unknown path -> dashboard (ProtectedRoute redirects to /login if needed) */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
